/**
 * The picture clean-up that makes text easy to read, as plain arithmetic on RGBA pixels (no screen needed,
 * so it runs in a background worker): grey, light letters on a dark background turned into dark on light,
 * then black-and-white per small area (Bradley–Roth adaptive threshold), so uneven light, shadows and
 * pictures behind the text don't hide the letters.
 */
export interface PrepOpts {
  maxSide?: number;
  window?: number;
  t?: number;
}

export function thresholdPixels(d: Uint8ClampedArray, w: number, h: number, opts: PrepOpts = {}) {
  const n = w * h;
  const grey = new Float32Array(n);
  let sum = 0;
  for (let i = 0, j = 0; j < n; i += 4, j++) {
    grey[j] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    sum += grey[j];
  }
  // Mostly dark picture: the letters are light — flip so they are dark.
  if (sum / n < 128) for (let j = 0; j < n; j++) grey[j] = 255 - grey[j];
  // A pixel is ink when clearly darker than its neighbourhood (an integral image keeps this fast).
  const integral = new Float64Array((w + 1) * (h + 1));
  for (let y = 1; y <= h; y++) {
    let row = 0;
    for (let x = 1; x <= w; x++) {
      row += grey[(y - 1) * w + (x - 1)];
      integral[y * (w + 1) + x] = integral[(y - 1) * (w + 1) + x] + row;
    }
  }
  const half = Math.max(8, Math.round(Math.max(w, h) / (opts.window ?? 48)));
  const T = opts.t ?? 0.2;
  for (let y = 0; y < h; y++) {
    const y1 = Math.max(0, y - half);
    const y2 = Math.min(h, y + half + 1);
    for (let x = 0; x < w; x++) {
      const x1 = Math.max(0, x - half);
      const x2 = Math.min(w, x + half + 1);
      const area = (x2 - x1) * (y2 - y1);
      const local = integral[y2 * (w + 1) + x2] - integral[y1 * (w + 1) + x2] - integral[y2 * (w + 1) + x1] + integral[y1 * (w + 1) + x1];
      const v = grey[y * w + x] * area < local * (1 - T) ? 0 : 255;
      const k = (y * w + x) * 4;
      d[k] = d[k + 1] = d[k + 2] = v;
    }
  }
}

/** Size the picture down for reading (quicker, and big enough for small print). */
export function readingSize(width: number, height: number, maxSide = 1800) {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { w: Math.round(width * scale), h: Math.round(height * scale) };
}
